import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtNum, fmtCur } from "@/lib/api";
import PeriodFilter from "@/components/PeriodFilter";
import { DollarSign, Landmark, TrendingUp, Users, Percent, AlertTriangle, Wallet } from "lucide-react";

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="grad-top rounded-md border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-[#2b9bb7]" />
      </div>
      <div className={`mt-3 font-heading text-2xl font-semibold tabular ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function Profit() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["profit", dateFrom, dateTo],
    queryFn: () => api.get("/profit", { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data),
  });

  const t = data?.totals;
  const profitColor = t && t.company_profit >= 0 ? "text-[#326F4B]" : "text-destructive";

  return (
    <div className="space-y-6" data-testid="profit-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Profit &amp; Commissions</h1>
          <p className="mt-1 text-sm text-muted-foreground">Net profit after bank costs and affiliate commissions, from imported transactions.</p>
        </div>
        <PeriodFilter dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} testId="profit-period" />
      </div>

      <div className="rounded-md border border-border bg-[#EDF3F5] px-4 py-3 text-xs text-muted-foreground" data-testid="profit-formula">
        <span className="font-semibold text-foreground">Formula:</span> Bank cost = amount × bank % + fixed &nbsp;·&nbsp; Net = fee revenue − bank cost &nbsp;·&nbsp; Affiliate commission = rate × net &nbsp;·&nbsp; Company profit = net − commission. Only <span className="font-semibold">completed</span> transactions are counted.
      </div>

      {t && t.count === 0 && (
        <div className="rounded-md border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="profit-empty">
          No completed transactions yet. Import a transactions export (Import Data → Transactions) to see profit.
        </div>
      )}

      {t && t.unmatched_bank > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-[#D68C3C]/10 px-4 py-2.5 text-sm text-[#A7773F]" data-testid="unmatched-warning">
          <AlertTriangle className="h-4 w-4" />
          {fmtNum(t.unmatched_bank)} transaction(s) have no matching bank fee rule (cost counted as 0). Add the provider in Settings → Banks &amp; Fees.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Stat label="Fee Revenue" value={isLoading ? "…" : fmtCur(t?.revenue)} icon={DollarSign} />
        <Stat label="Bank Costs" value={isLoading ? "…" : fmtCur(t?.bank_cost)} icon={Landmark} />
        <Stat label="Net (pre-commission)" value={isLoading ? "…" : fmtCur(t?.net)} icon={Wallet} />
        <Stat label="Affiliate Commissions" value={isLoading ? "…" : fmtCur(t?.commission)} icon={Users} />
        <Stat label="Company Net Profit" value={isLoading ? "…" : fmtCur(t?.company_profit)} icon={TrendingUp} accent={profitColor} />
        <Stat label="Transactions" value={isLoading ? "…" : fmtNum(t?.count)} icon={Percent} />
      </div>

      {/* By affiliate */}
      <div>
        <h3 className="mb-3 font-heading text-lg font-medium">Commission by Affiliate</h3>
        {/* Mobile: cards */}
        <div className="space-y-3 md:hidden" data-testid="affiliate-commission-cards">
          {(data?.by_affiliate || []).length === 0 ? (
            <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-muted-foreground">No data.</div>
          ) : (
            (data?.by_affiliate || []).map((a, i) => (
              <div key={i} className="rounded-md border border-border bg-card p-4" data-testid={`affiliate-comm-card-${i}`}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="truncate font-mono font-medium">{a.code}</span>
                  <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{a.rate}%</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Txns</div><div className="text-sm tabular">{fmtNum(a.count)}</div></div>
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Volume</div><div className="font-mono text-sm">{fmtCur(a.volume)}</div></div>
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Net</div><div className="font-mono text-sm">{fmtCur(a.net)}</div></div>
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Commission</div><div className="font-mono text-sm font-semibold text-[#2b9bb7]">{fmtCur(a.commission)}</div></div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="hidden overflow-x-auto rounded-md border border-border bg-card md:block">
          <table className="w-full text-sm" data-testid="affiliate-commission-table">
            <thead>
              <tr className="border-b border-border bg-[#EDF3F5] text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Affiliate</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rate</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Txns</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Volume</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Net</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Commission Owed</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_affiliate || []).length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No data.</td></tr>
              ) : (
                (data?.by_affiliate || []).map((a, i) => (
                  <tr key={i} className="border-b border-border hover:bg-[#E6EFF2]" data-testid={`affiliate-comm-row-${i}`}>
                    <td className="px-4 py-3 font-mono font-medium">{a.code}</td>
                    <td className="px-4 py-3">{a.rate}%</td>
                    <td className="px-4 py-3 text-right tabular">{fmtNum(a.count)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtCur(a.volume)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtCur(a.net)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular font-semibold text-[#2b9bb7]">{fmtCur(a.commission)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* By bank */}
      <div>
        <h3 className="mb-3 font-heading text-lg font-medium">Cost by Bank / Provider</h3>
        {/* Mobile: cards */}
        <div className="space-y-3 md:hidden" data-testid="bank-cost-cards">
          {(data?.by_bank || []).length === 0 ? (
            <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-muted-foreground">No data.</div>
          ) : (
            (data?.by_bank || []).map((b, i) => (
              <div key={i} className="rounded-md border border-border bg-card p-4" data-testid={`bank-cost-card-${i}`}>
                <div className="mb-3 truncate font-mono font-medium">{b.provider}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Txns</div><div className="text-sm tabular">{fmtNum(b.count)}</div></div>
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Volume</div><div className="font-mono text-sm">{fmtCur(b.volume)}</div></div>
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fee Revenue</div><div className="font-mono text-sm">{fmtCur(b.revenue)}</div></div>
                  <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Bank Cost</div><div className="font-mono text-sm text-destructive">{fmtCur(b.cost)}</div></div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="hidden overflow-x-auto rounded-md border border-border bg-card md:block">
          <table className="w-full text-sm" data-testid="bank-cost-table">
            <thead>
              <tr className="border-b border-border bg-[#EDF3F5] text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Provider</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Txns</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Volume</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fee Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bank Cost</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_bank || []).length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No data.</td></tr>
              ) : (
                (data?.by_bank || []).map((b, i) => (
                  <tr key={i} className="border-b border-border hover:bg-[#E6EFF2]" data-testid={`bank-cost-row-${i}`}>
                    <td className="px-4 py-3 font-mono font-medium">{b.provider}</td>
                    <td className="px-4 py-3 text-right tabular">{fmtNum(b.count)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtCur(b.volume)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtCur(b.revenue)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular text-destructive">{fmtCur(b.cost)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
